import { request } from '@/fetch/core';
import { Method } from '@/fetch/core/constant';
import { ApiResponse } from '@/types';

export interface VehicleOrderPageRequest {
  startTime?: string;
  endTime?: string;
  provinceId?: number | null;
  cityId?: number | null;
  countryId?: number | null;
  companyCode?: string;
  areaCode?: string;
  stationNumber?: string;
  vehicleModelType?: string;
  orderNumber?: string;
  status?: string;
  expectedDeliveryMonth?: string;
  pageNum: number;
  pageSize: number;
}

export interface DispatchVehicleRequest {
  id: number;
  driverName: string;
  idNumber?: string;
  contact: string;
  insuranceMoney?: number;
  vehiclePlateNumber?: string;
  vehicleModel?: string;
  estimatedArrivalTime: string;
  loadAddress?: string;
}

export interface AssignVehicleRequest {
  id: number;
  vehicleList: {
    vehicleName: string;
    serialNo: string;
    vehicleTypeId: number;
    vehicleTypeName: string;
    vehicleNumber?: string;
  }[];
}

export interface CheckVehicleRequest {
  id: number;
  checkUser: string;
  vehicleAppearanceStatus: boolean;
  vehiclePowerStatus: boolean;
  chargerEquippedStatus: boolean;
  spareKeyEquippedStatus: boolean;
  imageList?: {
    type?: string;
    fileKey?: string;
    bucketName?: string;
  }[];
  checkRemark?: string;
}

export interface EditOrderRequest {
  orderId: number;
  version: number;
  currentData: {
    userName: string;
    contact: string;
    idNumber?: string;
    insurance?: number;
    vehiclePlateNumber?: string;
    vehicleModel?: string;
    estimatedArrivalTime: string;
    loadAddress?: string;
  };
}

export interface PageUpdateLogRequest {
  orderId: number;
  pageNum: number;
  pageSize: number;
}

export interface singleOrderRequest {
  id: number;
}

// 订单详情 - 车辆信息
export interface VehicleOrderDetailVehicleName {
  /** 车辆序列号 */
  serialNo: string;
  /** 车辆名称 */
  vehicleName: string;
  /** 车牌号 */
  vehicleNumber: string;
  /** 车型ID */
  vehicleTypeId: number;
  /** 车型名称 */
  vehicleTypeName: string;
}

// 订单详情出参
export interface VehicleOrderDetailDTO {
  /** 地址 */
  address: string;
  /** 备用联系人 */
  alternateContact: string | null;
  /** 备用联系人电话 */
  alternateContactPhone: string | null;
  /** 区域名称 */
  areaName: string;
  /** 联系人 */
  contact: string;
  /** 联系人信息 */
  contactInfo: string | null;
  /** 联系电话 */
  contactPhone: string;
  /** 国家-市-区县 */
  contryName: string;
  /** 数量 */
  count: number;
  /** 司机姓名 */
  driverName: string | null;
  /** 预计到达时间 */
  estimatedArrivalTime: string | null;
  /** 预计交付月份 */
  expectedDeliveryMonth: string;
  /** 身份证号 */
  idNumber: string | null;
  /** 保险金额 */
  insurance: number | null;
  /** 保险单号 */
  insuranceNo: string;
  /** 装车地址 */
  loadAddress: string | null;
  /** 邮箱 */
  mail: string | null;
  /** 订单编号 */
  orderNumber: string;
  /** 站点名称 */
  stationName: string;
  /** 站点编码 */
  stationNumber: string;
  /** 供应商 */
  supplier: string;
  /** 供应商联系人 */
  supplierContact: string;
  /** 供应商用户 */
  supplierUser: string;
  /** 车型 */
  vehicleModel: string | null;
  /** 车型名称 */
  vehicleModelTypeName: string;
  /** 车辆列表 */
  vehicleNameList: VehicleOrderDetailVehicleName[];
  /** 车牌号 */
  vehiclePlateNumber: string | null;
  /** 版本号 */
  version: number;
}

class VehicleOrderManageApi {
  // 分页查询订单
  public async getVehicleOrderPage(
    params: VehicleOrderPageRequest,
  ): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/order/page',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 查看订单详情
  public async getDetailById(
    id: number,
  ): Promise<ApiResponse<VehicleOrderDetailDTO>> {
    const requestOptions = {
      path: '/k2/management/deployment/order/getDetailById',
      method: Method.POST,
      body: { id },
    };
    return request(requestOptions);
  }

  // 发运
  public async dispatchVehicle(params: DispatchVehicleRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/order/dispatchVehicle',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 配车
  public async assignVehicle(params: AssignVehicleRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/order/assignVehicle',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 验收
  public async checkVehicle(params: CheckVehicleRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/order/checkVehicle',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 订单编辑
  public async editOrder(params: EditOrderRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/order/edit',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 修改记录
  public async pageUpdateLog(params: PageUpdateLogRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/order/pageUpdateLog',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 到车
  public async arrivedVehicle(params: singleOrderRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/order/arrivedVehicle',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 订单交付
  public async completeOrder(params: singleOrderRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/order/completed',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 取消订单
  public async cancelOrder(params: {
    id: number;
    cancelReason: string;
  }): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/order/cancel',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }
}

export const vehicleOrderManageApi = new VehicleOrderManageApi();
